/**
 * 前端对 admin-portal server REST 契约的镜像。
 * 字段与 `apps/admin-portal/server/models/*.ts` + `routes/*.ts` 对齐；
 * 若 server 端契约改，本文件需同步更新（Phase A/B/C.1 还未 codegen）。
 */

export type OrgStatus = "active" | "suspended" | "archived";

/** admin-portal web JWT 三 scope（2026-07 org-admin）：super/support=平台超管，
 *  org-admin=客户企业管理员（仅可达 token 内 orgCode 的组织）。 */
export type AdminScope = "super" | "support" | "org-admin";

/**
 * 「能否管理当前组织」集中派生（2026-07-07 用户规则：超管=任何组织的管理员）。
 * org-admin 管自己 org（跨 org 由路由守卫/服务端 requireOrgAccess 挡）；
 * super/support 平台超管代任意组织管理。false 仅剩未登录/scope 异常。
 *
 * 注意与「限制 org-admin」的反向用法区分（BasicInfo readOnly / RolesPanel
 * highRiskReadOnly / 登录重定向守卫）——那些判断的是"是不是租户管理员"，
 * 不是"谁能管理"，不要用本 helper 替换。
 */
export function canManageOrg(scope: AdminScope | undefined): boolean {
  return scope === "org-admin" || scope === "super" || scope === "support";
}

/** Organization — `GET /api/organizations` / `:code` 返回形状 */
export interface Organization {
  /**
   * T1.2.2 后 server serializePublicOrganization 使用 `id`（string，不带 ObjectId 前缀），
   * 不再返 mongo 原始 `_id`。前端把字段名同步过来。
   */
  id: string;
  name: string;
  code: string;
  status: OrgStatus;
  maxEmployees: number;
  expiresAt?: string;
  dosiaProvisioning?: {
    bytespikeOrgCode?: string;
    importedBy?: string;
    importedAt?: string;
  };
  /**
   * 客户端实际消费的 feishu 子字段（appSecretEncrypted / encryptKey / verificationToken /
   * cachedTenantAccessToken 不在此声明，前端永不读）。
   */
  feishu?: {
    appId?: string;
    tenantKey?: string;
    lastTokenRefresh?: string;
    tokenExpiresAt?: string;
  };
  /**
   * T1.2.2 · 仅 POST /api/organizations 201 响应里出现一次：sk_ 前缀 + 32 bytes hex
   * 明文 client API token，UI 弹窗展示 + 复制后再不可恢复。GET list/:code/PATCH
   * 响应里**不存在**此字段（serializer 剥）。
   */
  apiToken?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Phase G · 长连接状态机（SDK `WSConnectionState` 透传）。
 *   - idle:         未启动（org 未配凭据 / 服务刚起还没轮到）
 *   - connecting:   首次 handshake 进行中
 *   - connected:    handshake 完成，事件可推
 *   - reconnecting: 断线后重连循环中
 *   - failed:       重试耗尽 / 致命错（onError 触发）
 */
export type FeishuLongConnState =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "failed";

export interface FeishuLongConnectionInfo {
  state: FeishuLongConnState;
  connectCount: number;
  lastConnectedAt?: string;
  lastReconnectingAt?: string;
  lastError?: string;
  appId?: string;
}

/**
 * `GET /api/organizations/:orgCode/feishu/status` 返回形状。
 * 字段集与 `apps/admin-portal/server/routes/orgs-feishu.ts` GET /status 一致：
 * server 不返 appId（节省载荷；admin 已知自己刚填的 appId）。
 *
 * Phase G · 新增 `longConnection` 子段反映 WSClient 实时状态；
 * 未启用长连接管理器（如测试 build-app）时该字段缺省。
 */
export interface FeishuStatus {
  connected: boolean;
  tenantKey?: string;
  tokenExpiresAt?: string;
  lastTokenRefresh?: string;
  departmentCount: number;
  boundUserCount: number;
  last24hErrors: number;
  longConnection?: FeishuLongConnectionInfo;
}

/** `POST /api/organizations/:orgCode/feishu/connect|sync` summary */
export interface RosterSyncResult {
  departments: number;
  usersUpdated: number;
  errors: string[];
}

/**
 * `PUT /api/organizations/:orgCode/feishu/credentials` 请求 body。
 *
 * Phase G · 长连接模式下只有 `appId` + `appSecret` 必填；
 * `encryptKey` / `verificationToken` / `tenantKey` 仅 HTTP webhook fallback 模式需要。
 */
export interface FeishuCredentials {
  appId: string;
  appSecret: string;
  encryptKey?: string;
  verificationToken?: string;
  tenantKey?: string;
}

/** `POST /api/admin/login` / `/api/admin/org-login` 返回（orgCode 仅 org-login 有） */
export interface LoginResponse {
  token: string;
  scope: AdminScope;
  orgCode?: string;
  expiresAt: string;
}

export interface AuthState {
  token: string;
  scope: AdminScope;
  /** scope === "org-admin" 时必有；导航裁剪与跨 org 前端守卫用 */
  orgCode?: string;
  expiresAt: string;
}

/** `GET /api/orgs/:orgCode/bootstrap` 返回（Phase A mock） */
export interface BootstrapPayload {
  org: {
    code: string;
    name: string;
    dosiaProvisioning?: { bytespikeOrgCode?: string };
  };
  employees: unknown[];
  roles: unknown[];
  leadershipMap: Record<string, string[]>;
  etag?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** 结构化错误负载（如 400 校验返回的 `violations` 数组）——调用方按需消费，缺省 undefined。 */
  readonly details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// ============================================================
// Phase C.2 — admin-portal-web 6 模块前端 API 契约镜像。
// 字段与 apps/admin-portal/server/routes/orgs-{users,roles,leadership,audit,feishu}.ts
// + services/serializers.ts 对齐。后端契约变更需同步更新。
// ============================================================

export type UserStatus = "active" | "disabled" | "pending";
export type AuditSource = "ui" | "api" | "webhook" | "sync" | "agent";

export interface PublicFeishu {
  openId?: string;
  unionId?: string;
  name?: string;
  departmentIds?: string[];
}

export interface PublicUser {
  id: string;
  user: string;
  /** 登录唯一键（手机号，2026-07）。无 = 该员工暂无法登录，需补录。 */
  mobile?: string;
  status: UserStatus;
  /** roleCode（由后端 serializePublicEmployee populate 后 emit） */
  role?: string;
  roleId?: string;
  feishu?: PublicFeishu;
  /** T1.3.8 · 是否启用 dosia desktop；legacy 行无此字段视为 false */
  enableDosia: boolean;
  dosiaEmployeeId?: string;
}

export interface UsersListResponse {
  total: number;
  page: number;
  pageSize: number;
  rows: PublicUser[];
}

export interface CreateUserBody {
  user: string;
  /** 登录唯一键（手机号，2026-07）。提供须 11 位数字且 org 内唯一。 */
  mobile?: string;
  password: string;
  roleId?: string;
  status?: UserStatus;
  /** T1.3.8 · enableDosia=true 触发 server auto-gen dosiaEmployeeId */
  enableDosia?: boolean;
  dosiaEmployeeId?: string;
}

export interface PatchUserBody {
  /** 改登录名（admin 可覆盖自动生成的拼音）。不可为 openId；重名返 409。 */
  user?: string;
  status?: UserStatus;
  /** null → $unset roleId */
  roleId?: string | null;
  /** T1.3.8 · false → true 触发 auto-gen；true → false 不清 dosiaEmployeeId */
  enableDosia?: boolean;
  /** T1.3.9 · 仅在当前为空时允许；非空时 PATCH 此字段返 400 immutable */
  dosiaEmployeeId?: string;
}

/** POST /api/organizations/:orgCode/users/batch-patch request */
export interface BatchPatchUserBody {
  userIds: string[];
  patch: {
    enableDosia?: boolean;
  };
}

/** POST /api/organizations/:orgCode/users/batch-patch response */
export interface BatchPatchUserResponse {
  modified: number;
  skipped: number;
  errors: Array<{ userId: string; error: string }>;
}

/** 密码下发渠道（2026-07）：feishu 私信 / copy 明文复制。 */
export type DeliverChannel = "feishu" | "copy";

/** 单用户密码下发结果（单/批量共用 item）。 */
export interface DeliverPasswordResult {
  userId: string;
  user: string;
  mobile?: string;
  /** copy 渠道命中时返一次明文临时密码。 */
  generatedPassword?: string;
  /** feishu 渠道送达结果（未选该渠道则缺省）。 */
  feishu?: { ok: boolean; reason?: string };
}

/** POST /:userId/deliver-password request */
export interface DeliverPasswordBody {
  channels: DeliverChannel[];
}

/** POST /deliver-password（批量）request */
export interface BatchDeliverPasswordBody {
  userIds: string[];
  channels: DeliverChannel[];
}

/** POST /deliver-password（批量）response */
export interface BatchDeliverPasswordResponse {
  results: DeliverPasswordResult[];
  errors: Array<{ userId: string; error: string }>;
}

export interface BindFeishuBody {
  openId: string;
  unionId?: string;
  name?: string;
  mobile?: string;
  email?: string;
  avatarUrl?: string;
  departmentIds?: string[];
}

// ---- Roles ----

/** 角色飞书 bot 配置（secret 只回 hasSecret，永不下发明文）。 */
export interface RoleFeishuBotPublic {
  enabled: boolean;
  appId?: string;
  hasSecret: boolean;
}

export interface PublicRole {
  id: string;
  code: string;
  name: string;
  description?: string;
  isSystem: boolean;
  /** RoleWorkspaceConfig（极深嵌套；Phase C.2 用 JSON editor） */
  dosia?: unknown;
  suggestedFeishuDeptIds: string[];
  feishuBot?: RoleFeishuBotPublic;
}

export interface CreateRoleBody {
  code: string;
  name: string;
  description?: string;
  dosia?: unknown;
  suggestedFeishuDeptIds?: string[];
}

/** feishuBot patch：appSecret 留空 = 不改原密文（前端不知明文）。 */
export interface RoleFeishuBotPatch {
  enabled?: boolean;
  appId?: string;
  appSecret?: string;
}

export interface PatchRoleBody {
  name?: string;
  description?: string;
  dosia?: unknown;
  suggestedFeishuDeptIds?: string[];
  feishuBot?: RoleFeishuBotPatch;
}

// ---- Leadership ----

export interface LeadershipRow {
  roleCode: string;
  leaderUserIds: string[];
  updatedAt: string;
}

export interface UpsertLeadershipBody {
  leaderUserIds: string[];
}

// ---- Project Admins（N-76 R6.1）----

/** 名单存 DOSIA 用户名（`PublicUser.user`），不是 `PublicUser.id`——企业端按用户名比对。
 *  Leadership 的 `leaderUserIds` 存 user._id（管理台按 id 编辑），下发给企业端时由
 *  后端 `buildLeadershipMap` 统一换成用户名（N-98）。 */
export interface ProjectAdminsResponse {
  users: string[];
}

export interface PutProjectAdminsBody {
  users: string[];
}

// ---- Audit ----

export interface AuditLogRow {
  id: string;
  actor: string;
  action: string;
  source: AuditSource;
  target?: string;
  before?: unknown;
  after?: unknown;
  at: string;
}

export interface AuditListResponse {
  total: number;
  page: number;
  pageSize: number;
  rows: AuditLogRow[];
}

// ---- Feishu dept tree ----

export interface FeishuDeptNode {
  openDepartmentId: string;
  name: string;
  parentOpenDepartmentId?: string;
  memberCount: number;
  boundUserCount: number;
  /** 飞书部门组长 open_id（wizard Step2 标「组长」） */
  leaderUserIds: string[];
  children: FeishuDeptNode[];
}

export interface FeishuDepartmentsResponse {
  tree: FeishuDeptNode[];
  syncedAt: string | null;
}

/** `GET /api/organizations/:orgCode/feishu/departments/:deptId/members` 单条成员 */
export interface FeishuDeptMember {
  openId: string;
  unionId?: string;
  name?: string;
  mobile?: string;
  email?: string;
  avatarUrl?: string;
  departmentIds?: string[];
  /** openId ∈ 该部门 leaderUserIds */
  isLeader: boolean;
  alreadyImported: boolean;
  importedUser?: { id: string; user: string };
}

export interface FeishuDeptMembersResponse {
  members: FeishuDeptMember[];
}

// ---- Pinyin migration ----

export type PinyinMatchReason =
  | "mobile-exact"
  | "email-exact"
  | "email-prefix"
  | "pinyin-full"
  | "pinyin-initial";

export interface PinyinCandidate {
  openId: string;
  name?: string;
  mobile?: string;
  email?: string;
  score: number;
  reason: PinyinMatchReason;
}

export interface PinyinProposal {
  userId: string;
  user: string;
  confirmed: boolean;
  candidates: PinyinCandidate[];
}

export interface PinyinProposalsBundle {
  orgCode: string;
  orgId: string;
  generatedAt: string;
  summary: {
    unboundUsers: number;
    withCandidates: number;
    noCandidates: number;
    /** 飞书通讯录拉到的人数；0 会被后端 dry-run 拦成 409 feishu-roster-empty，不会到这里。 */
    feishuMirrors: number;
  };
  proposals: PinyinProposal[];
}

export interface PinyinApplyResult {
  written: number;
  skipped: number;
}

// ============================================================
// Phase F — Agent 化角色推荐 + per-org LLM 配置。
// 字段与 apps/admin-portal/server/routes/{role-provisioning,orgs-llm-config}.ts
// + services/serializers.ts + models/role-provisioning-task.ts 对齐。
// ============================================================

export type RpStatus =
  | "pending"
  | "analyzing"
  | "awaiting-confirm"
  | "confirmed"
  | "rejected";

export type RpType = "initial-bootstrap" | "new-employee" | "manual-adjust";

export type LlmProvider = "anthropic" | "openai-compat";

/** 后端 IRoleSuggestion 镜像（每条推荐角色）。 */
export interface RoleSuggestion {
  code: string;
  name: string;
  description?: string;
  /** RoleWorkspaceConfig — 与 PublicRole.dosia 同形态，前端不深解。 */
  dosia?: unknown;
  suggestedFeishuDeptIds: string[];
  assignedUserIds: string[];
  confidence?: number;
  reason?: string;
}

export interface RoleProvisioningSuggestion {
  roles: RoleSuggestion[];
  unassignedUserIds?: string[];
  llmModel: string;
  llmProvider: string;
  generatedAt: string;
}

export interface FeishuDeptSnapshot {
  deptIds: string[];
  hits?: string[];
}

export interface RoleProvisioningTask {
  id: string;
  orgId: string;
  type: RpType;
  triggeredBy: string;
  status: RpStatus;
  affectedUserIds: string[];
  feishuDeptSnapshot?: FeishuDeptSnapshot;
  suggestion?: RoleProvisioningSuggestion;
  feishuCardMessageId?: string;
  confirmedBy?: string;
  confirmedAt?: string;
  appliedAt?: string;
  createdAt: string;
  error?: string;
}

export interface RoleProvisioningListResponse {
  total: number;
  page: number;
  pageSize: number;
  rows: RoleProvisioningTask[];
}

export interface StartRpBody {
  type: "initial-bootstrap" | "manual-adjust";
  affectedUserIds?: string[];
}

export interface StartRpResponse {
  taskId: string;
  type: RpType;
  status: RpStatus;
}

export interface ApplyRpBody {
  adjustedSuggestion?: RoleProvisioningSuggestion;
}

export interface RejectRpBody {
  reason?: string;
}

/** GET /api/organizations/:orgCode/llm-config 返回（hasKey:boolean，永不返 raw key） */
export interface LlmProviderConfig {
  provider: LlmProvider;
  model: string;
  baseUrl?: string;
  hasKey: boolean;
  configuredBy?: string;
  configuredAt?: string;
}

export interface UpsertLlmConfigBody {
  provider: LlmProvider;
  /** 留空保留原 key（仅 hasKey=true 时合法） */
  apiKey?: string;
  model: string;
  baseUrl?: string;
}

export interface LlmTestResult {
  ok: boolean;
  latencyMs?: number;
  error?: string;
}

/**
 * GET /api/organizations/:orgCode/cloud-feishu-app 返回（hasSecret:boolean，永不返 appSecret）。
 * 云端定时任务执行飞书操作用的 tenant 应用身份登记——与本页「集成与凭据」的腾讯文档状态
 * 走 dosia-enterprise 透传不同，本条走 admin-portal 自有 Mongo（镜像 llm-config 形态）。
 */
export interface CloudFeishuAppStatus {
  appId: string | null;
  hasSecret: boolean;
}

export interface UpsertCloudFeishuAppBody {
  appId: string;
  /** 留空保留原 secret（仅 hasSecret=true 时合法） */
  appSecret?: string;
}

// ============================================================
// 组织搭建（onboarding）引导进度。
// 字段与 apps/admin-portal/server/routes/orgs-onboarding.ts 对齐。
// ============================================================

/** `GET /api/organizations/:orgCode/onboarding` 返回 */
export interface OrgOnboarding {
  currentStep: number;
  completedSteps: number[];
  completedAt: string | null;
  totalSteps: number;
}

export interface PatchOnboardingBody {
  currentStep?: number;
  completeStep?: number;
  reset?: boolean;
}

// ============================================================
// Skill Hub 平台 Catalog 管理（PR-3）—— 镜像 apps/web skill-hub store admin view。
// 浏览器 API：/api/platform/skill-hub/*（admin-portal 透传 enterprise service face）。
// ============================================================

export type SkillHubItemKind = "skill" | "plugin";
export type SkillHubItemStatus = "draft" | "published" | "unpublished";
export type SkillHubRevisionValidationStatus = "uploaded" | "validated" | "invalid";

/** Catalog 条目 metadata（admin 写入面；与 store.SkillHubItemMetadata 对齐）。 */
export interface SkillHubItemMetadata {
  name: string;
  description: string;
  longDescription?: string;
  category: string;
  tags: string[];
  useCases?: string[];
  minDosiaVersion?: string;
  providedByPlugin?: string;
  featuredRank?: number;
}

export interface SkillHubRevisionView {
  revision: string;
  version: string;
  sha256: string;
  archiveBytes: number;
  validationStatus: SkillHubRevisionValidationStatus;
  validationError: string | null;
  createdAt: string;
  createdBy: string;
  validatedAt: string | null;
  validatedBy: string | null;
}

export interface SkillHubAuditEventView {
  eventId: string;
  action: string;
  actor: string;
  requestId: string | null;
  createdAt: string;
  oldSnapshot: unknown;
  newSnapshot: unknown;
}

/** `GET/POST …/skill-hub/items` 成功 data 形状。 */
export interface SkillHubAdminItemView {
  itemId: string;
  slug: string;
  kind: SkillHubItemKind;
  metadata: SkillHubItemMetadata;
  status: SkillHubItemStatus;
  currentRevision: string | null;
  previousRevision: string | null;
  draftRevision: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  publicUpdatedAt: string;
  publishedAt: string | null;
  revisions: SkillHubRevisionView[];
  recentAudit: SkillHubAuditEventView[];
}

/** 上游成功信封（列表 / 单项 mutation 共用）。 */
export interface SkillHubSuccessEnvelope<T> {
  success: true;
  data: T;
}
